import { useMemo, useRef, useState, type KeyboardEvent } from 'react';
import { useNavigate, useParams } from 'react-router';
import { Loader2, Zap } from 'lucide-react';
import { toast } from 'sonner';
import { PRIORITY_COLOURS, parseQuickAdd, type QuickAddToken } from '@virtuetasks/types';
import { Dialog, DialogContent, DialogTitle } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { ApiError } from '@/lib/api';
import { cn, formatMinutes } from '@/lib/utils';
import { useUiStore } from '@/stores/ui';
import { useAuth } from '@/features/auth';
import { useProject } from '@/features/projects';
import {
  TypeaheadMenu,
  useTokenTypeahead,
} from '@/components/shared/TokenTypeahead';
import { useUsers } from '@/features/settings/api';
import { useProjects } from '@/features/projects';
import { useQuickAdd } from './api';

const TOKEN_COLOUR: Record<QuickAddToken['kind'], string> = {
  assignee: 'var(--accent)',
  project: 'var(--accent)',
  priority: 'var(--danger)',
  type: 'var(--warn)',
  due: 'var(--p2)',
  estimate: 'var(--text-muted)',
};

/**
 * `⌘N` from anywhere. Uses the **same parser as the server**, so what the
 * highlighting shows is exactly what will be created (§5-F4).
 */
export function QuickAddDialog() {
  const open = useUiStore((s) => s.quickAddOpen);
  const setOpen = useUiStore((s) => s.setQuickAddOpen);
  const navigate = useNavigate();
  const { key } = useParams();
  const { user } = useAuth();
  const { data: contextProject } = useProject(key);
  const quickAdd = useQuickAdd();

  const [text, setText] = useState('');
  const [error, setError] = useState<string | null>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  // R2: the picker inserts the canonical token text, so the shared parser below
  // stays the single source of truth for what will actually be created.
  const { data: people = [] } = useUsers();
  const { data: allProjects = [] } = useProjects();
  const typeahead = useTokenTypeahead({
    value: text,
    onChange: (next) => setText(next),
    inputRef,
    sources: {
      people: people.filter((p) => p.status === 'active'),
      projects: allProjects,
    },
  });

  const parsed = useMemo(() => (text ? parseQuickAdd(text) : null), [text]);

  async function submit(andOpen: boolean) {
    if (!text.trim()) return;
    setError(null);
    try {
      const task = await quickAdd.mutateAsync({
        text: text.trim(),
        projectId: contextProject?._id ?? user?.preferences.defaultProjectId ?? undefined,
      });
      toast.success(`${task.key} created`);
      setText('');
      setOpen(false);
      if (andOpen) navigate(`/tasks/${task.key}`);
    } catch (err) {
      if (err instanceof ApiError && err.code === 'PROJECT_REQUIRED') {
        setError(err.message);
      } else {
        setError(err instanceof ApiError ? err.message : 'Could not create the task');
      }
    }
  }

  function onKeyDown(event: KeyboardEvent<HTMLInputElement>) {
    // The menu gets first refusal on ↑↓/Enter/Tab/Esc so Enter picks a
    // suggestion rather than submitting a half-typed token.
    if (typeahead.onKeyDown(event)) return;
    if (event.key !== 'Enter') return;
    event.preventDefault();
    void submit(event.shiftKey);
  }

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogContent
        className="w-[min(640px,calc(100vw-24px))]"
        // Escape with suggestions open dismisses them, not the dialog. Radix
        // handles Escape in the capture phase, so this is the only place that
        // can decide it.
        onEscapeKeyDown={(e) => {
          if (typeahead.open) e.preventDefault();
        }}
      >
        <DialogTitle className="mb-1 flex items-center gap-1.5 text-[15px] font-semibold">
          <Zap className="size-4 text-[var(--accent)]" />
          Quick add
        </DialogTitle>
        <p className="mb-3 text-[11px] vt-muted">
          <span className="vt-mono">@person</span> · <span className="vt-mono">#KEY</span> ·{' '}
          <span className="vt-mono">!p0</span> · <span className="vt-mono">~bug</span> ·{' '}
          <span className="vt-mono">tomorrow</span> · <span className="vt-mono">+3h</span>
        </p>

        <div className="relative">
          <Input
            autoFocus
            ref={inputRef}
            value={text}
            onChange={typeahead.onInputChange}
            onKeyUp={typeahead.onCaretMove}
            onClick={typeahead.onCaretMove}
            onKeyDown={onKeyDown}
            role="combobox"
            aria-expanded={typeahead.open}
            aria-autocomplete="list"
            aria-label="Quick add"
            data-testid="quick-add-input"
            placeholder="Fix login crash on Android @paras #VL !p0 ~bug tomorrow +3h"
            className="h-10 text-[14px]"
          />
          <TypeaheadMenu state={typeahead} className="left-0 top-11" />
        </div>

        {parsed && (
          <div className="mt-2 flex flex-wrap items-center gap-1.5 text-[11px]">
            <span className="vt-muted">Will create:</span>
            <span className="font-medium">{parsed.title || '(no title yet)'}</span>
            {parsed.tokens.map((token, index) => (
              <span
                key={`${token.kind}-${index}`}
                className={cn('rounded-full px-1.5 py-0.5')}
                style={{ background: 'var(--surface-2)', color: TOKEN_COLOUR[token.kind] }}
                title={`${token.kind}: ${token.value}`}
              >
                {token.kind === 'estimate'
                  ? formatMinutes(Number(token.value))
                  : token.kind === 'priority'
                    ? token.value
                    : token.raw}
              </span>
            ))}
            {parsed.priority && (
              <span
                className="size-2 rounded-full"
                style={{ background: PRIORITY_COLOURS[parsed.priority] }}
                aria-hidden
              />
            )}
          </div>
        )}

        {!parsed?.projectKey && !contextProject && !user?.preferences.defaultProjectId && (
          <p className="mt-2 rounded-[6px] bg-[var(--warn)]/10 px-2 py-1.5 text-[11px] text-[var(--warn)]">
            No project in context — add <span className="vt-mono">#KEY</span>, or set a default
            project in Settings.
          </p>
        )}

        {error && (
          <p role="alert" className="mt-2 rounded-[6px] bg-[var(--danger)]/10 px-2 py-1.5 text-[12px] text-[var(--danger)]">
            {error}
          </p>
        )}

        <div className="mt-3 flex items-center justify-end gap-2">
          <span className="mr-auto text-[11px] vt-muted">
            <kbd className="vt-mono">↵</kbd> create · <kbd className="vt-mono">⇧↵</kbd> create and open
          </span>
          <Button variant="ghost" onClick={() => setOpen(false)}>
            Cancel
          </Button>
          <Button
            variant="primary"
            onClick={() => void submit(false)}
            disabled={!text.trim() || quickAdd.isPending}
          >
            {quickAdd.isPending ? <Loader2 className="animate-spin" /> : null}
            Create
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
